import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ProductService } from '../services/ProductService';
import { ProductApiAdapter } from '../adapters/ProductApiAdapter';
import { Product } from '../models/Product';

export interface UseProductsInfiniteFilters {
  searchTerm?: string;
  limit?: number; // por defecto 10
  enabled?: boolean; // por defecto true
  tenantChildId?: number;
  /** Respuesta ligera: solo id, name, code, image_hash */
  fields?: 'basic';
}

export const useProductsInfinite = (filters: UseProductsInfiniteFilters = {}) => {
  const productService = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    const adapter = new ProductApiAdapter(httpClient);
    return new ProductService(adapter);
  }, []);

  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } =
    useInfiniteQuery({
      queryKey: ['products-infinite', filters.searchTerm, filters.limit, filters.tenantChildId, filters.fields],
      queryFn: ({ pageParam = 1 }) =>
        productService.getProductsPaginated({
          searchTerm: filters.searchTerm,
          page: pageParam,
          limit: filters.limit ?? 10,
          tenantChildId: filters.tenantChildId,
          fields: filters.fields,
        }),
      getNextPageParam: (lastPage) => lastPage.nextPage,
      initialPageParam: 1,
      staleTime: 5 * 60 * 1000,
      enabled: filters.enabled !== false,
    });

  const products: Product[] = data?.pages.flatMap((p: any) => p.products) ?? [];
  const total = data?.pages?.[0]?.total ?? 0;

  return {
    products,
    total,
    isLoading: isLoading || isPending,
    error,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  };
};
